// frontend/src/hooks/useNotifications.ts - NEW NOTIFICATION-SPECIFIC HOOKS
// Entity-specific hooks for notifications separated from core useApi.ts
// Insert this as a new file: useNotifications.ts

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  
  // Notification types
  Notification,
  NotificationQueryParams,
  NotificationType,
  NotificationCategory,
  Priority,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== LOCAL INTERFACES =====

/**
 * Notification settings interface
 */
interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  inAppNotifications: boolean;
  notificationTypes: {
    CORRESPONDENCE_CREATED: boolean;
    RESPONSE_ASSIGNED: boolean;
    RESPONSE_APPROVED: boolean;
    RESPONSE_REJECTED: boolean;
    WORKFLOW_STAGE_CHANGED: boolean;
    GROUP_MEMBERSHIP_CHANGED: boolean;
    PROJECT_ASSIGNED: boolean;
    DOCUMENT_UPLOADED: boolean;
    SYSTEM_ALERT: boolean;
  };
  quietHours?: {
    enabled: boolean;
    startTime: string; // HH:mm format
    endTime: string; // HH:mm format
    timezone: string;
  };
  digestFrequency: 'none' | 'daily' | 'weekly';
}

/**
 * Notification preferences update request
 */
interface NotificationPreferencesUpdateRequest {
  emailNotifications?: boolean;
  pushNotifications?: boolean;
  inAppNotifications?: boolean;
  notificationTypes?: Partial<NotificationSettings['notificationTypes']>;
  quietHours?: NotificationSettings['quietHours'];
  digestFrequency?: NotificationSettings['digestFrequency'];
}

/**
 * Notification statistics interface
 */
interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
  byCategory: Record<string, number>;
  todayCount: number;
  weekCount: number;
  monthCount: number;
}

/**
 * Notification summary interface
 */
interface NotificationSummary {
  unreadCount: number;
  totalCount: number;
  recentNotifications: Notification[];
  priorityBreakdown: Record<Priority, number>;
  typeBreakdown: Record<NotificationType, number>;
  categoryBreakdown: Record<NotificationCategory, number>;
}

/**
 * Batch notification operation request
 */
interface BatchNotificationOperation {
  action: 'markRead' | 'markUnread' | 'delete' | 'dismiss';
  notificationIds: string[];
  reason?: string;
}

/**
 * Unread count response
 */
interface UnreadCountResponse {
  count: number;
  byPriority: Record<Priority, number>;
  byCategory: Record<NotificationCategory, number>;
}

// ===== NOTIFICATION QUERY HOOKS =====

/**
 * Fetch paginated list of notifications with comprehensive filtering
 */
export const useNotifications = (params?: NotificationQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Notification>> => {
      
      const response = await apiClient.notifications.list(params);
      
      return normalizePaginatedResponse<Notification>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'notifications_list',
      ...options
    }
  );
};

/**
 * Fetch single notification by ID with details
 */
export const useNotification = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<Notification> => {
      validateObjectId(id, 'Notification ID');
      
      
      const response = await apiClient.notifications.get(id!);
      
      return normalizeSingleResponse<Notification>(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `notification_${id}`,
      ...options
    }
  );
};

/**
 * Get unread notification count
 */
export const useUnreadNotificationCount = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<UnreadCountResponse> => {
      
      // Use summary endpoint to get unread count
      const response = await apiClient.notifications.getSummary();
      
      const summaryData = normalizeSingleResponse<NotificationSummary>(response);
      
      return {
        count: summaryData.unreadCount || 0,
        byPriority: summaryData.priorityBreakdown || {},
        byCategory: summaryData.categoryBreakdown || {}
      };
    },
    {
      immediate: true,
      rateLimitKey: 'notification_unread_count',
      ...options
    }
  );
};

/**
 * Get notification settings/preferences
 */
export const useNotificationSettings = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<NotificationSettings> => {
      
      const response = await apiClient.notifications.getSettings();
      
      return normalizeSingleResponse<NotificationSettings>(response);
    },
    {
      immediate: true,
      rateLimitKey: 'notification_settings',
      ...options
    }
  );
};

// ===== NOTIFICATION MUTATION HOOKS =====

/**
 * Mark notification as read
 */
export const useMarkNotificationRead = (options?: UseMutationOptions<Notification, string>) => {
  return useMutation(
    async (id: string): Promise<Notification> => {
      validateObjectId(id, 'Notification ID');
      
      
      const response = await apiClient.notifications.markAsRead(id);
      
      return normalizeSingleResponse<Notification>(response);
    },
    options
  );
};

/**
 * Mark all notifications as read
 */
export const useMarkAllNotificationsRead = (options?: UseMutationOptions<void, void>) => {
  return useMutation(
    async (): Promise<void> => {
      
      const response = await apiClient.notifications.markAllAsRead();
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Delete notification
 */
export const useDeleteNotification = (options?: UseMutationOptions<void, string>) => {
  return useMutation(
    async (id: string): Promise<void> => {
      validateObjectId(id, 'Notification ID');
      
      
      const response = await apiClient.notifications.delete(id);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Dismiss notification (soft delete/hide)
 */
export const useDismissNotification = (options?: UseMutationOptions<void, { id: string; reason?: string }>) => {
  return useMutation(
    async ({ id, reason }: { id: string; reason?: string }): Promise<void> => {
      validateObjectId(id, 'Notification ID');
      
      
      // Since there's no explicit dismiss endpoint, we use delete
      // In a real implementation, you might want a separate dismiss endpoint
      const response = await apiClient.notifications.delete(id);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Update notification settings
 */
export const useUpdateNotificationSettings = (options?: UseMutationOptions<NotificationSettings, NotificationPreferencesUpdateRequest>) => {
  return useMutation(
    async (settings: NotificationPreferencesUpdateRequest): Promise<NotificationSettings> => {
      
      const response = await apiClient.notifications.updateSettings(settings);
      
      return normalizeSingleResponse<NotificationSettings>(response);
    },
    options
  );
};

// ===== BATCH OPERATIONS =====

/**
 * Batch mark notifications as read
 */
export const useBatchMarkNotificationsRead = (options?: UseMutationOptions<void, string[]>) => {
  return useMutation(
    async (ids: string[]): Promise<void> => {
      if (!ids || ids.length === 0) {
        throw new Error('At least one notification ID is required');
      }
      
      validateObjectIds(ids, 'Notification IDs');
      
      
      const response = await apiClient.notifications.batchMarkAsRead(ids);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Batch delete notifications
 */
export const useBatchDeleteNotifications = (options?: UseMutationOptions<void, string[]>) => {
  return useMutation(
    async (ids: string[]): Promise<void> => {
      if (!ids || ids.length === 0) {
        throw new Error('At least one notification ID is required');
      }
      
      validateObjectIds(ids, 'Notification IDs');
      
      
      const response = await apiClient.notifications.batchDelete(ids);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Generic batch notification operations
 */
export const useBatchNotificationOperations = (options?: UseMutationOptions<void, BatchNotificationOperation>) => {
  return useMutation(
    async (operation: BatchNotificationOperation): Promise<void> => {
      // Validate operation
      if (!operation.notificationIds || operation.notificationIds.length === 0) {
        throw new Error('At least one notification ID is required');
      }
      
      validateObjectIds(operation.notificationIds, 'Notification IDs');
      
      if (!['markRead', 'markUnread', 'delete', 'dismiss'].includes(operation.action)) {
        throw new Error('Invalid operation action');
      }
      
      
      let response;
      
      switch (operation.action) {
        case 'markRead':
          response = await apiClient.notifications.batchMarkAsRead(operation.notificationIds);
          break;
        case 'delete':
        case 'dismiss':
          response = await apiClient.notifications.batchDelete(operation.notificationIds);
          break;
        case 'markUnread':
          // This would need to be implemented if the API supports it
          throw new Error('Mark as unread operation not yet implemented');
        default:
          throw new Error(`Unsupported batch operation: ${operation.action}`);
      }
      
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

// ===== ANALYTICS AND STATISTICS HOOKS =====

/**
 * Get notification statistics
 */
export const useNotificationStats = (params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<NotificationStats> => {
      
      const response = await apiClient.notifications.getSummary();
      
      const summaryData = normalizeSingleResponse<NotificationSummary>(response);
      
      // Transform summary into stats format
      const stats: NotificationStats = {
        total: summaryData.totalCount || 0,
        unread: summaryData.unreadCount || 0,
        read: (summaryData.totalCount || 0) - (summaryData.unreadCount || 0),
        byType: summaryData.typeBreakdown || {},
        byPriority: summaryData.priorityBreakdown || {},
        byCategory: summaryData.categoryBreakdown || {},
        todayCount: 0, // Would need separate endpoint or calculation
        weekCount: 0,  // Would need separate endpoint or calculation
        monthCount: 0  // Would need separate endpoint or calculation
      };
      
      return stats;
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      rateLimitKey: 'notification_stats',
      ...options
    }
  );
};

/**
 * Get notification summary (recent notifications + counts)
 */
export const useNotificationSummary = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<NotificationSummary> => {
      
      const response = await apiClient.notifications.getSummary();
      
      return normalizeSingleResponse<NotificationSummary>(response);
    },
    {
      immediate: true,
      rateLimitKey: 'notification_summary',
      ...options
    }
  );
};

// ===== SPECIALIZED UTILITY HOOKS =====

/**
 * Hook for notification polling (real-time updates)
 */
export const useNotificationPolling = (intervalMs: number = 30000, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<NotificationSummary> => {
      const response = await apiClient.notifications.getSummary();
      return normalizeSingleResponse<NotificationSummary>(response);
    },
    {
      immediate: true,
      dependencies: [intervalMs],
      rateLimitKey: 'notification_polling',
      ...options
    }
  );
};

/**
 * Hook for notification validation and helpers
 */
export const useNotificationHelpers = () => {
  const validateNotificationData = useCallback((notification: Partial<Notification>): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!notification.title?.trim()) {
      errors.push('Notification title is required');
    }
    
    if (!notification.message?.trim()) {
      errors.push('Notification message is required');
    }
    
    if (!notification.type) {
      errors.push('Notification type is required');
    }
    
    if (!notification.category) {
      errors.push('Notification category is required');
    }
    
    if (!notification.priority) {
      errors.push('Notification priority is required');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }, []);
  
  const formatNotificationTime = useCallback((timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day}-${months[date.getMonth()]}-${date.getFullYear()}`;
  }, []);
  
  const getPriorityColor = useCallback((priority: Priority): string => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  }, []);
  
  const getNotificationIcon = useCallback((type: NotificationType): string => {
    switch (type) {
      case 'info': return '🔔';
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      default: return '🔔';
    }
  }, []);
  
  return {
    validateNotificationData,
    formatNotificationTime,
    getPriorityColor,
    getNotificationIcon
  };
};

// ===== BACKWARD COMPATIBILITY ALIASES =====

// These aliases maintain backward compatibility with existing code
export const useNotificationPreferences = useNotificationSettings;
export const useUpdateNotificationPreferences = useUpdateNotificationSettings;